/**
 * LandingHero — dark-section hero with the vertical chips, the
 * brand badge, the two-clause display headline (orange accent on
 * the second clause), the subhead, the per-vertical line, the two
 * primary CTAs, and a small "click the door to sign in" hint under
 * the LogoDoor entry-point.
 *
 * Visual structure mirrors NoRework's hero — same chip → badge →
 * headline → subhead → CTAs vertical stack — but copy is original
 * and the entry affordance is the LogoDoor component (the user's
 * actual brand artwork).
 */

import { type ReactNode } from 'react'
import { LogoDoor }       from '@/components/brand/LogoDoor'
import {
  HERO_BADGE,
  HERO_HEADLINE_1,
  HERO_HEADLINE_2,
  HERO_TAGLINE,
  VERTICALS,
  VERTICAL_LABELS,
  VERTICAL_COPY,
}                         from './landingCopy'
import type { Vertical }  from '@/types/landing'

export interface LandingHeroProps {
  vertical:         Vertical
  onVerticalChange: (v: Vertical) => void
  onPrimaryCta:     () => void
  onSecondaryCta:   () => void
  onSignIn:         () => void
}

export function LandingHero({
  vertical,
  onVerticalChange,
  onPrimaryCta,
  onSecondaryCta,
  onSignIn,
}: LandingHeroProps): ReactNode {
  const verticalCopy = VERTICAL_COPY[vertical]

  return (
    <header className="landing-hero">
      <div className="landing-hero__inner">
        <div role="tablist" aria-label="Property type" className="landing-hero__chips">
          {VERTICALS.map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={vertical === v}
              className={`landing-chip ${vertical === v ? 'landing-chip--active' : ''}`}
              onClick={() => onVerticalChange(v)}
            >
              {VERTICAL_LABELS[v]}
            </button>
          ))}
        </div>

        <span className="landing-hero__badge">{HERO_BADGE}</span>

        <h1 className="landing-hero__headline">
          {HERO_HEADLINE_1}{' '}
          <span className="landing-hero__headline-accent">{HERO_HEADLINE_2}</span>
        </h1>

        <p className="landing-hero__subhead">{HERO_TAGLINE}</p>
        <p className="landing-hero__vertical-line">{verticalCopy.heroLine}</p>

        <div className="landing-hero__ctas">
          <button type="button" className="landing-cta landing-cta--primary" onClick={onPrimaryCta}>
            Get a quote
          </button>
          <button type="button" className="landing-cta landing-cta--ghost-light" onClick={onSecondaryCta}>
            See how it works
          </button>
        </div>

        <div style={{ marginTop: '2.5rem' }}>
          <LogoDoor size={140} onClick={onSignIn} ariaLabel="Sign in to ReadyRoomOps" />
          <p className="landing-hero__door-hint">Already a customer? Click the door to sign in.</p>
        </div>
      </div>
    </header>
  )
}
